import { useCallback } from "react";
import { getBrandMetrics } from "@/api/client";
import { CsvDownloadButton } from "@/components/CsvDownloadButton";
import { DataTable, type Column } from "@/components/DataTable";
import { EngagementCharts } from "@/components/EngagementCharts";
import { MultiFilterSelect } from "@/components/MultiFilterSelect";
import { MeterCard } from "@/components/MeterCard";
import { StatCard } from "@/components/StatCard";
import { ChartSkeleton, ErrorState, TableSkeleton } from "@/components/states";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useApiData } from "@/hooks/useApiData";
import { useProductFilterOptions } from "@/hooks/useProductFilters";
import { useUrlListFilter } from "@/hooks/useUrlFilters";
import {
  formatCompact,
  formatDate,
  formatNumber,
  formatPercent,
} from "@/lib/format";

function share(brandUnits: number, categoryUnits: number): number | null {
  return categoryUnits > 0 ? brandUnits / categoryUnits : null;
}

function shareCell(brandUnits: number, categoryUnits: number): string {
  const s = share(brandUnits, categoryUnits);
  return s === null ? "—" : formatPercent(s, 2);
}

type DmaRow = { dma: string; brandUnits: number; categoryUnits: number };
type WeekRow = { weekStart: string; brandUnits: number; categoryUnits: number };

// Short ranked tables — no per-column sorting, but still CSV-exportable
// (csvValue carries the export since there's no sortValue to fall back on).
const dmaColumns: Column<DmaRow>[] = [
  { header: "DMA", cell: (r) => r.dma, csvValue: (r) => r.dma },
  {
    header: "Brand units",
    align: "right",
    cell: (r) => formatNumber(r.brandUnits),
    csvValue: (r) => r.brandUnits,
  },
  {
    header: "Category units",
    align: "right",
    cell: (r) => formatNumber(r.categoryUnits),
    csvValue: (r) => r.categoryUnits,
  },
  {
    header: "Market share",
    align: "right",
    cell: (r) => shareCell(r.brandUnits, r.categoryUnits),
    csvValue: (r) => share(r.brandUnits, r.categoryUnits),
  },
];

const weekColumns: Column<WeekRow>[] = [
  {
    header: "Week start",
    cell: (r) => formatDate(r.weekStart),
    csvValue: (r) => r.weekStart,
  },
  {
    header: "Brand units",
    align: "right",
    cell: (r) => formatNumber(r.brandUnits),
    csvValue: (r) => r.brandUnits,
  },
  {
    header: "Category units",
    align: "right",
    cell: (r) => formatNumber(r.categoryUnits),
    csvValue: (r) => r.categoryUnits,
  },
  {
    header: "Market share",
    align: "right",
    cell: (r) => shareCell(r.brandUnits, r.categoryUnits),
    csvValue: (r) => share(r.brandUnits, r.categoryUnits),
  },
];

export function BrandMetricsPage() {
  const filters = useProductFilterOptions();
  const [brandIds, setBrandIds, brand] = useUrlListFilter("brand");
  // Key "subcategory": the options are level-2 categories sent as
  // subcategoryId, and sibling pages use "category" for level-1 ids.
  const [categoryIds, setCategoryIds, category] =
    useUrlListFilter("subcategory");

  const fetchMetrics = useCallback(
    (token: string) =>
      getBrandMetrics(token, { brandId: brand, subcategoryId: category }),
    [brand, category],
  );
  const state = useApiData(fetchMetrics);
  const data = state.status === "ready" ? state.data : null;

  return (
    <div className="flex flex-col gap-6" aria-busy={state.status === "loading"}>
      <div>
        <h1 className="text-xl font-semibold">Brand metrics</h1>
        <p className="text-sm text-muted-foreground">
          Brand units against its category over the last 90 days
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <MultiFilterSelect
          label="Brand"
          allLabel="All brands"
          values={brandIds}
          onChange={setBrandIds}
          disabled={filters.disabled}
          options={filters.brandOptions}
        />
        <MultiFilterSelect
          label="Category"
          allLabel="All categories"
          values={categoryIds}
          onChange={setCategoryIds}
          disabled={filters.disabled}
          options={filters.categoryOptions}
        />
      </div>

      {state.status === "error" ? (
        <ErrorState message={state.message} onRetry={state.reload} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Brand units"
              value={data && formatCompact(data.totals.brandUnits)}
            />
            <StatCard
              label="Category units"
              value={data && formatCompact(data.totals.categoryUnits)}
            />
            <MeterCard
              label="Category market share"
              value={
                data
                  ? share(data.totals.brandUnits, data.totals.categoryUnits)
                  : undefined
              }
            />
            <MeterCard
              label="Basket penetration"
              value={data ? data.basketPenetration : undefined}
            />
          </div>

          <Card>
            <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
                <CardTitle>Units by DMA</CardTitle>
                <CardDescription>
                  Brand vs category units per market
                </CardDescription>
              </div>
              {data ? (
                <CsvDownloadButton
                  name="brand-units-by-dma"
                  columns={dmaColumns}
                  rows={data.byDma}
                />
              ) : null}
            </CardHeader>
            <CardContent>
              {data ? (
                <DataTable
                  columns={dmaColumns}
                  rows={data.byDma}
                  rowKey={(r) => r.dma}
                  emptyMessage="No units in range"
                />
              ) : (
                <TableSkeleton />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
                <CardTitle>Units by week</CardTitle>
                <CardDescription>Monday-start weeks</CardDescription>
              </div>
              {data ? (
                <CsvDownloadButton
                  name="brand-units-by-week"
                  columns={weekColumns}
                  rows={data.byWeek}
                />
              ) : null}
            </CardHeader>
            <CardContent>
              {data ? (
                <DataTable
                  columns={weekColumns}
                  rows={data.byWeek}
                  rowKey={(r) => r.weekStart}
                  emptyMessage="No units in range"
                />
              ) : (
                <TableSkeleton />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Shopper engagement</CardTitle>
              <CardDescription>
                Shoppers of the selected brand, bucketed by transactions in
                these ranges: heavy 13+, medium 2–12, light 1
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data ? (
                <EngagementCharts engagement={data.engagement} />
              ) : (
                <ChartSkeleton height={180} />
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
